/** Klien API. Cookie sesi dikirim otomatis (HttpOnly, tidak terbaca JS). */

export interface ApiErrorBody {
  code: string;
  message: string;
  fields?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized() {
    return this.status === 401;
  }
}

const BASE = '/api/v1';

/**
 * Dipanggil saat SEMBARANG permintaan dijawab 401.
 *
 * Tanpa ini, sesi yang kedaluwarsa di tengah pemakaian tidak terlihat: sidebar
 * masih menampilkan nama pengguna dan halaman masih penuh data lama, tetapi
 * setiap penyimpanan gagal dengan pesan yang tidak bisa ditindaklanjuti —
 * pengguna diminta "masuk kembali" tanpa diberi jalan ke sana. Sesi mati harus
 * langsung tampak, bukan ditemukan satu per satu lewat tombol yang gagal.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Tidak dapat terhubung ke server. Periksa koneksi Anda.');
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => null);

  if (!res.ok) {
    // Login yang gagal juga menjawab 401, tetapi itu bukan sesi yang mati —
    // mengeluarkan pengguna dari halaman login karena salah sandi jelas keliru.
    if (res.status === 401 && !path.startsWith('/auth/login')) onUnauthorized?.();

    const err = (body as { error?: ApiErrorBody } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? 'Terjadi kesalahan.',
      err?.fields,
    );
  }

  return (body as { data: T }).data;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body ?? {}),
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
